import React, { useRef, useState, useEffect } from 'react';
import NDVIChart from '../Monitoring/NDVIChart';
import {
    GoogleMap,
    useLoadScript,
    Marker,
    Polygon,
    InfoWindow
} from '@react-google-maps/api';

const containerStyle = { width: '100%', height: '600px' };
const defaultCenter = { lat: 22.5937, lng: 78.9629 };

export const BhuAgriMap = () => {
    const inputRef = useRef();
    const polygonRef = useRef();

    const [center, setCenter] = useState(defaultCenter);
    const [marker, setMarker] = useState(null);
    const [clickedMarker, setClickedMarker] = useState(null);
    const [polygonPath, setPolygonPath] = useState([]);
    const [fieldId, setFieldId] = useState(null);
    const [amedData, setAmedData] = useState(null);
    const [infoWindowPos, setInfoWindowPos] = useState(null);
    const [clickedAddress, setClickedAddress] = useState('');
    const [mapType, setMapType] = useState("satellite");

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "AIzaSyC16Y-R8bhKWx57M63qEGelyGB3SRfxFIU",
    });

    // ----- SEARCH LOCATION -----
    const handleSearch = async () => {
        const place = inputRef.current.value;
        if (!place) return alert("Enter address");
        const apiKey = process.env.REACT_APP_GOOGLE_API_KEY || "AIzaSyC16Y-R8bhKWx57M63qEGelyGB3SRfxFIU";
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(place)}&key=${apiKey}`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.status === "OK" && data.results.length > 0) {
            const location = data.results[0].geometry.location;
            setCenter(location);
            setMarker(location);
            setClickedMarker(location);
            setInfoWindowPos(location);
            setClickedAddress(data.results[0].formatted_address);
            await handleAuntkrishiAPIs(location.lat, location.lng);
        } else alert("Location not found");
    };

    // ----- MAP CLICK -----
    const handleMapClick = async (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const clicked = { lat, lng };
        setClickedMarker(clicked);
        setCenter(clicked);
        setInfoWindowPos(clicked);
        setClickedAddress("Loading...");
        try {
            const apiKey = process.env.REACT_APP_GOOGLE_API_KEY || "AIzaSyC16Y-R8bhKWx57M63qEGelyGB3SRfxFIU";
            const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
            const response = await fetch(url);
            const data = await response.json();
            if (data.status === "OK" && data.results.length > 0) {
                setClickedAddress(data.results[0].formatted_address);
            } else setClickedAddress("Address not found");
        } catch {
            setClickedAddress("Geocode failed");
        }
        await handleAuntkrishiAPIs(lat, lng);
    };

    // ----- AUNTKRISHI ALU & AMED -----
    const handleAuntkrishiAPIs = async (lat, lng) => {
        try {
            // ALU
            const aluRes = await fetch(`https://auntkrishi.com/api/alu/demo?lat=${18.699899638316907}&lng=${lng}`);
            const aluData = await aluRes.json();
            const polygon = aluData.polygonCoordinates.map(([lat, lng]) => ({ lat, lng }));
            setPolygonPath(polygon);
            setFieldId(aluData.fieldId);

            // AMED
            const amedRes = await fetch(`https://auntkrishi.com/api/amed?fieldId=${aluData.fieldId}`);
            const amed = await amedRes.json();
            setAmedData(amed);
        } catch (err) {
            console.error("Auntkrishi API Error:", err);
            alert("Failed to fetch data from Auntkrishi API");
        }
    };

    // ----- POLYGON LOAD & EDIT -----
    const onPolygonLoad = (polygon) => { polygonRef.current = polygon; };

    const handlePolygonEdit = async () => {
        if (!polygonRef.current) return;
        const newPath = polygonRef.current.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
        setPolygonPath(newPath);

        // Use polygon center as new lat/lng for AMED refresh
        const centerLat = newPath.reduce((sum, p) => sum + p.lat, 0) / newPath.length;
        const centerLng = newPath.reduce((sum, p) => sum + p.lng, 0) / newPath.length;
        if (fieldId) await handleAuntkrishiAPIs(centerLat, centerLng);
    };

    return (
        <div style={{ padding: 20 }}>
            <h3>🌾 Smart Agriculture Dashboard – Field Mapping & Monitoring</h3>
            <div style={{ marginBottom: 10, display: "flex", gap: "8px" }}>
                <input ref={inputRef} type="text" placeholder="Enter village, city, or coordinates"
                    style={{ flex: 1, padding: "8px", fontSize: "14px", borderRadius: "4px", border: "1px solid #ccc" }} />
                <button onClick={handleSearch} style={{ padding: "8px 12px", backgroundColor: "#99cc22", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>🔍 Search</button>
                <button onClick={() => setMapType("satellite")} style={{ padding: "8px 10px", backgroundColor: mapType === "satellite" ? "#2980b9" : "#bdc3c7", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>🛰️ Satellite</button>
                <button onClick={() => setMapType("roadmap")} style={{ padding: "8px 10px", backgroundColor: mapType === "roadmap" ? "#2980b9" : "#bdc3c7", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>🗺️ Roadmap</button>
            </div>

            {isLoaded ? (
                <div style={{ display: "flex", gap: "20px" }}>
                    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={18} onClick={handleMapClick} mapTypeId={mapType}>
                        {marker && <Marker position={marker} />}
                        {clickedMarker && <Marker position={clickedMarker} label="📍" />}
                        {infoWindowPos && <InfoWindow position={infoWindowPos} onCloseClick={() => setInfoWindowPos(null)}>
                            <div>
                                <strong>📌 Address:</strong><br />
                                {clickedAddress}<br />
                                <small>Lat: {infoWindowPos.lat.toFixed(5)}<br />Lng: {infoWindowPos.lng.toFixed(5)}</small>
                            </div>
                        </InfoWindow>}
                        {polygonPath.length > 0 && (
                            <Polygon paths={polygonPath} editable={true} onLoad={onPolygonLoad} onMouseUp={handlePolygonEdit} onDragEnd={handlePolygonEdit} options={{
                                fillColor: '#00FF0088', strokeColor: '#00FF00', strokeWeight: 2
                            }} />
                        )}
                    </GoogleMap>

                    <div style={{ flex: 1, padding: "20px", backgroundColor: "#f4f6f8", border: "1px solid #ccc", borderRadius: "8px" }}>
                        <h3 style={{ color: "#2c3e50", marginBottom: "15px" }}>🛰️ Monitoring Panel</h3>
                        {fieldId && <p><strong>Field ID:</strong> {fieldId}</p>}
                        {amedData && amedData.ndviHistory && (
                            <div style={{ marginTop: 20 }}>
                                <h5>NDVI Trend</h5>
                                <NDVIChart ndviData={amedData.ndviHistory} />
                            </div>
                        )}
                    </div>
                </div>
            ) : (<p>Loading Map...</p>)}

            {fieldId && (
                <div style={{ marginTop: 20 }}>
                    <h4>🧾 Field ID: {fieldId}</h4>
                    <p>🌱 Crop: {amedData?.crop}</p>
                    <p>🌿 NDVI: {amedData?.ndvi}</p>
                    <p>💧 Soil Moisture: {amedData?.moisture}</p>
                    <p>🕒 Last Updated: {amedData?.lastUpdate}</p>
                </div>
            )}
        </div>
    );
};

export default BhuAgriMap;
